/**
 * W-16 聊天页（story AC-6/7/8/9）。master-detail 双栏（左会话列表 + 右消息流）。
 * 视觉真源 design/chat.html。实时收发经 useChat（WebSocket + ImFrame）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listConversations, markConversationRead, type ConversationItem } from '@/api/im';
import { call } from '@/api/client';
import type { components } from '@/api/openapi.generated';
import { useChat } from '@/im/useChat';
import type { ImConnectionState } from '@/im/connection';
import { CHAT_CONTENT_MAX_LENGTH } from '@/im/frame';
import { ConversationRow } from '@/components/im/ConversationRow';
import { ChatBubbleView } from '@/components/im/ChatBubbleView';
import { CONVERSATIONS_QUERY_KEY } from './ConversationsPage';
import { UNIT_LABEL } from '@/lib/productLabels';

type ProductDetailResponse = components['schemas']['ProductDetailResponse'];

/** 金额以分计 → ¥ 元（对齐 App formatMoney）。 */
function formatYuan(cents: number | undefined): string {
  const value = (cents ?? 0) / 100;
  return `¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATE_LABEL: Record<ImConnectionState, string> = {
  CONNECTING: '连接中...',
  CONNECTED: '已连接',
  RECONNECTING: '断线重连中...',
  CLOSED: '连接已关闭',
  DISCONNECTED: '未连接',
};

interface ChatRouteState {
  peerUserId?: string;
  peerNickname?: string;
  peerAvatar?: string;
  seedProductId?: string;
}

export function ChatPage() {
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const routeState = (location.state ?? {}) as ChatRouteState;

  const {
    data: listData,
    isError: listError,
    refetch: refetchList,
  } = useQuery({
    queryKey: CONVERSATIONS_QUERY_KEY,
    queryFn: listConversations,
  });
  const conversations = listData?.items ?? [];

  // 对端信息:优先 route state,缺失(深链/刷新)时从会话列表回退。
  const fromList = conversations.find((c) => c.conversationId === conversationId);
  const peerNickname = routeState.peerNickname ?? fromList?.peerNickname;
  const seedProductId = routeState.seedProductId ?? fromList?.seedProductId;

  // seed 商品 brief:拉成功才渲染上下文卡(chat.md W-16)。
  const { data: seedBrief } = useQuery({
    queryKey: ['im', 'seed-product', seedProductId],
    queryFn: () =>
      call<ProductDetailResponse>({
        method: 'GET',
        url: `/api/v1/guide/product-detail/${encodeURIComponent(seedProductId as string)}`,
      }),
    enabled: !!seedProductId,
    staleTime: 5 * 60_000,
  });
  const peerDisplay =
    peerNickname && peerNickname.trim() !== ''
      ? peerNickname
      : `用户 ${routeState.peerUserId ?? fromList?.peerUserId ?? ''}`.trim();

  const {
    bubbles,
    dismissedBanners,
    connState,
    historyStatus,
    currentUserId,
    canSend,
    send,
    retry,
    dismissBanner,
    reloadHistory,
  } = useChat(conversationId);

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // 进会话标记已读(静默幂等);离开约 1s 后刷新列表未读(留出后端 markRead 落库窗口)。
  useEffect(() => {
    if (conversationId) markConversationRead(conversationId).catch(() => {});
    return () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
      }, 1000);
    };
  }, [conversationId, queryClient]);

  // 新消息贴底。
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [bubbles.length]);

  const dismissedSet = useMemo(() => new Set(dismissedBanners), [dismissedBanners]);

  const handleSelect = (item: ConversationItem) => {
    navigate(`/chat/${item.conversationId}`, {
      state: {
        peerUserId: item.peerUserId,
        peerNickname: item.peerNickname,
        peerAvatar: item.peerAvatar,
        seedProductId: item.seedProductId,
      },
    });
  };

  const submit = () => {
    if (!canSend || draft.trim() === '') return;
    send(draft);
    setDraft('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const historyEmpty = historyStatus === 'ready' && bubbles.length === 0;
  const historyErrored = historyStatus === 'error' && bubbles.length === 0;
  const historyLoading = historyStatus === 'loading' && bubbles.length === 0;

  return (
    <div className="inner section">
      <div className="chat-layout">
        {/* 左栏:会话列表 */}
        <aside className="conv-list">
          {listError && conversations.length === 0 ? (
            <div className="state" data-testid="chat-list-error">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => refetchList()}
              >
                加载失败,点击重试
              </button>
            </div>
          ) : (
            conversations.map((c) => (
              <ConversationRow
                key={c.conversationId}
                item={c}
                active={c.conversationId === conversationId}
                onSelect={handleSelect}
              />
            ))
          )}
        </aside>

        {/* 右栏:消息流 */}
        <section className="msg-stream">
          <div
            className="row"
            style={{
              padding: 'var(--space-4)',
              borderBottom: '1px solid var(--color-border)',
              gap: 'var(--space-3)',
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              aria-label="返回"
              onClick={() => navigate('/conversations')}
            >
              ←
            </button>
            <span className="avatar avatar-sm">{peerDisplay.slice(0, 1) || '?'}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 'var(--weight-semibold)' }}>{peerDisplay}</div>
              <div className="xs muted">{STATE_LABEL[connState]}</div>
            </div>
          </div>

          {seedProductId && seedBrief && (
            <button
              type="button"
              className="row between"
              onClick={() => navigate(`/product/${seedProductId}`)}
              style={{
                margin: 'var(--space-3) var(--space-4) 0',
                padding: 'var(--space-3)',
                background: 'var(--color-muted)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                width: 'auto',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span className="row" style={{ gap: 'var(--space-3)', minWidth: 0 }}>
                <span className="avatar" style={{ borderRadius: 'var(--radius-sm)' }}>
                  {seedBrief.title?.slice(0, 1) ?? '商'}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span
                    className="sm"
                    style={{
                      fontWeight: 'var(--weight-medium)',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {seedBrief.title}
                  </span>
                  <span className="price" style={{ fontSize: 'var(--text-base-size)' }}>
                    {formatYuan(seedBrief.price)}
                    {seedBrief.unit && (
                      <span className="unit">/{UNIT_LABEL[seedBrief.unit] ?? seedBrief.unit}</span>
                    )}
                  </span>
                </span>
              </span>
              <span className="sm muted" style={{ whiteSpace: 'nowrap' }}>
                查看相关商品 →
              </span>
            </button>
          )}

          <div className="msg-scroll" ref={scrollRef}>
            {historyLoading ? (
              <div className="state" data-testid="chat-history-loading">
                <div className="ic">⏳</div>
                <p className="muted sm">加载中…</p>
              </div>
            ) : historyErrored ? (
              <div className="state" data-testid="chat-history-error">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => reloadHistory()}
                >
                  加载失败,点击重试
                </button>
              </div>
            ) : historyEmpty ? (
              <div className="state" data-testid="chat-empty">
                <div className="ic">👋</div>
                <p className="muted sm">发送第一条消息开始对话</p>
              </div>
            ) : (
              bubbles.map((b) => (
                <ChatBubbleView
                  key={b.clientMsgId}
                  bubble={b}
                  isMine={b.senderId === currentUserId}
                  bannerDismissed={dismissedSet.has(b.clientMsgId)}
                  onDismissBanner={dismissBanner}
                  onRetry={retry}
                />
              ))
            )}
          </div>

          <div className="chat-input">
            <textarea
              className="textarea"
              rows={1}
              maxLength={CHAT_CONTENT_MAX_LENGTH}
              placeholder={canSend ? '输入消息…(最多 500 字)' : '连接中,暂不可发送'}
              value={draft}
              disabled={!canSend}
              onChange={(e) => setDraft(e.target.value.slice(0, CHAT_CONTENT_MAX_LENGTH))}
              onKeyDown={onKeyDown}
              style={{ flex: 1, resize: 'none' }}
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canSend || draft.trim() === ''}
              onClick={submit}
            >
              发送
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
